import React from 'react';
import { Button } from '@/components/ui/button';
import Field from './Fields/Field';
import HeaderSubtramite from './HeaderSubtramite';
import AppLayout from './Layouts/AppLayout';
import OtherFileList from './OtherFileList';
import SubtramiteContainer from './SubtramiteContainer';
import { useGoBackSimple } from '../hooks/useGoBack';
import { CheckCircle, RotateCcw } from 'lucide-react';
import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';

interface FieldConfig {
  type: string;
  name: string;
  label: string;
  visible?: boolean;
  readOnly?: boolean;
  helpText?: string;
  inline?: boolean;
  path?: string;
  options?: any[];
  maxWidth?: string;
}

interface SubtramiteFormProps {
  title: string;
  data: any;
  loading: boolean;
  init: boolean;
  isUpdate: boolean;
  setIsUpdate: (value: boolean) => void;
  handleChange: ({ name, value }: { name: string; value: any }) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleFinalizarSubtramite?: () => void;
  handleReabrirSubtramite?: () => void;
  btnGuardarVisible: boolean;
  fields: FieldConfig[];
  fileFields?: FieldConfig[];
  subtramiteKey: string;
  finalizarButtonText?: string;
  canUploadOtros?: boolean;
  canViewOtros?: boolean;
  editShow?: boolean;
}

const SubtramiteForm: React.FC<SubtramiteFormProps> = ({
  title,
  data,
  loading,
  init,
  isUpdate,
  setIsUpdate,
  handleChange,
  handleSubmit,
  btnGuardarVisible,
  fields,
  fileFields = [],
  subtramiteKey,
  handleFinalizarSubtramite = () => { },
  handleReabrirSubtramite = () => { },
  finalizarButtonText = 'Finalizar',
  canUploadOtros = false,
  canViewOtros = false,
  editShow,
}) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);
  const goBack = useGoBackSimple();

  return (
    <AppLayout
      title={title}
      prevPath='/dashboard'
    >
      <SubtramiteContainer>
        <HeaderSubtramite
          data={data}
          editShow={editShow ?? canManageTramites}
          onClickEdit={() => setIsUpdate(!isUpdate)}
          visible={isUpdate}
          subtramiteKey={subtramiteKey}
        />
        <form onSubmit={handleSubmit}>
          {/* Campos básicos */}
          {fields.map((field) => (
            <div key={field.name} className="my-2">
              <Field
                type={field.type}
                name={field.name}
                label={field.label}
                inline={field.inline}
                value={data[field.name]}
                onChange={handleChange}
                visible={field.visible}
                readOnly={field.readOnly}
                helpText={field.helpText}
                path={field.path}
                options={field.options || []}
                maxWidth={field.maxWidth || '100%'}
              />
            </div>
          ))}

          {isUpdate && <hr />}

          {/* Campos de archivos */}
          {fileFields.map((field) => (
            <div key={field.name} className="my-2">
              <Field
                type={field.type}
                name={field.name}
                label={field.label}
                inline={field.inline}
                value={data[field.name]}
                path={data[`${field.name}_path`]}
                onChange={handleChange}
                readOnly={field.readOnly}
                helpText={field.helpText}
                visible={field.visible}
                options={field.options || []}
                maxWidth={field.maxWidth || '100%'}
              />
            </div>
          ))}

          {/* Lista de otros archivos */}
          {isUpdate && (canViewOtros || canUploadOtros) && (
            <OtherFileList
              tramite_id={data.tramite_id}
              subtramite={subtramiteKey}
              value={data.files}
              categoria={data.categoria}
              onChange={({ files, fieldsDelete, filesDelete }) => {
                handleChange({
                  name: 'files',
                  value: files,
                });
                handleChange({
                  name: 'fieldsDelete',
                  value: fieldsDelete,
                });
                handleChange({
                  name: 'filesDelete',
                  value: filesDelete,
                });
              }}
              canUpload={canUploadOtros}
              canView={canViewOtros}
            />
          )}

          {/* Botones de acción */}
          <div className="pt-5">
            <div className="flex gap-2">
              <Button
                type='button'
                variant="outline"
                loading={loading}
                onClick={goBack}
              >
                Cancelar
              </Button>
              {(btnGuardarVisible) && (
                <Button type="submit" variant="outline" loading={loading}>
                  {init ? 'Guardar' : 'Iniciar'}
                </Button>
              )}

              {/* Botón para finalizar/reabrir subtramite - Solo visible para tramitadoras */}
              {canManageTramites && data.estado && (
                <>
                  {data.estado === 'en-proceso' && (
                    <Button
                      type="button"
                      variant="default"
                      loading={loading}
                      onClick={handleFinalizarSubtramite}
                      className="bg-green-100 text-green-800 hover:bg-green-700 hover:text-white"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {finalizarButtonText}
                    </Button>
                  )}
                  {data.estado === 'finalizado' && (
                    <Button
                      type="button"
                      variant="default"
                      loading={loading}
                      onClick={handleReabrirSubtramite}
                      className="bg-blue-100 text-blue-800 hover:bg-blue-700 hover:text-white"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reabrir
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </form>
      </SubtramiteContainer>
    </AppLayout>
  );
};

export default SubtramiteForm;
